<template>
  <div class="relative flex" ref="dropdownRef">
    <button
      @click="toggleDropdown"
      class="cursor-pointer"
    >
      <!-- Dynamic Profile Image with Fallback -->
      <div class="w-8 h-8 rounded-full overflow-hidden">
        <!-- Show actual image if photo exists -->
        <img
            v-if="miniProfile.profilePicture.value && miniProfile.profilePicture.value !== ''"
            :src="getValidProfileURL(miniProfile.profilePicture.value)"
            alt="Profile"
            class="w-full h-full rounded-full object-cover"
            @error="handleImgError"
        />
        <!-- Show initials avatar if no photo -->
        <div
            v-else
            :class="[
              'w-full h-full rounded-full flex items-center justify-center text-white font-semibold text-xs',
              userAvatarInfo.bgColor
            ]"
        >
          {{ userAvatarInfo.initials }}
        </div>
      </div>
    </button>

    <div
        v-show="isProfileDropdown" class="absolute right-0 mt-2 w-[343px] bg-white border border-grey rounded-md shadow-lg z-50 p-4 flex flex-col items-start transition-opacity duration-200 ease-in-out select-none"
    >
      <!-- Loading State -->
      <div v-if="loading" class="w-full flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>

      <!-- Content -->
      <div v-else class="w-full">
        <UserProfileCard 
          :expert-user="expertUser" 
          :expand-account-info="expandAccountInfo"
          @toggle-expand="expandAccountInfo = !expandAccountInfo"
        />

        <hr class="border-t border-gray-300 w-full my-4" />

        <!-- Menu Items -->
        <div class="text-primary w-full gap-8">
          <button
              v-if="shouldShowOldDashboardSwitch"
              @click="switchToOldDashboard"
              class="cursor-pointer flex items-center px-4 py-2 text-left gap-2 w-full hover:bg-gray-100 group"
          >
            <Overview />
            <div class="flex w-full justify-between">
              <span class="text-paragraph font-normal">New Dashboard</span>
              <div class="relative w-[46px] h-card-padding-md">
                <UiTooltip content="Switch to Old Dashboard">
                  <SwitchOn class="absolute inset-0 opacity-100 transition-opacity duration-300" />
                </UiTooltip>
              </div>
            </div>
          </button>
          <router-link to="/expert/settings"  @click="isProfileDropdown = false">
            <button class="cursor-pointer w-full bg-white hover:bg-gray-100 px-4 flex items-center gap-2 py-2">
              <SettingsIcon />
              <span class="text-paragraph font-normal">Settings</span>
            </button>
          </router-link>

          <router-link
            v-if="showPayoutsLink"
            to="/expert/wallet?tab=payouts"
            class="cursor-pointer w-full bg-white hover:bg-gray-100 px-4 flex items-center gap-2 py-2"
            @click="isProfileDropdown = false"
          >
            <PayoutIcon />
            <span class="text-paragraph font-normal">Payouts</span>
          </router-link>

          <button @click="logout()" class="cursor-pointer w-full bg-white hover:bg-gray-100 px-4 flex items-center gap-2 py-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white">
            <LogoutIcon />
            <span class="text-paragraph font-normal">Logout</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useFeatureFlags } from '@/app/composables/useFeatureFlags'
import { useCurrentExpertOrgId, useExpertWallet } from '@/app/composables/wallet'
import { useAuthStore } from '@/store/auth.ts'
import { onClickOutside } from '@vueuse/core'
import { handleImgError, generateInitialsAvatar } from "@/utils/helpers.ts"
import { getValidProfileURL } from '@/pages/expert/listing/utils/getValidProfileURL'
import { useExpertStore } from '@/store/expert'
import { useExpertMiniProfile } from '@/app/composables/experts/useExpertMiniProfile'
import { useUserVersion } from '@/app/composables/experts/useUserVersion'
import SettingsIcon from '@/assets/icons/settings.svg'
import LogoutIcon from '@/assets/icons/logout.svg'
import PayoutIcon from '@/assets/icons/payout.svg'
import SwitchOn from '@/assets/icons/switch-on.svg'
import Overview from "@/assets/icons/overview.svg";
import UserProfileCard from '@/components/expert/UserProfileCard.vue'
import { UiTooltip } from '@shopexperts/shared'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const expertStore = useExpertStore();

const { isExpertWalletEnabled } = useFeatureFlags()
const walletEnabled = computed(() => isExpertWalletEnabled(route.query))
const { orgId } = useCurrentExpertOrgId()
const { allowPayouts } = useExpertWallet({ actingOrgId: orgId, enabled: walletEnabled })
const showPayoutsLink = computed(() => walletEnabled.value && allowPayouts.value)

// Use the mini profile composable for profile picture and name
const miniProfile = useExpertMiniProfile()

// Use the user version composable to determine dashboard features
const { version: userVersion } = useUserVersion()

const isProfileDropdown = ref(false)
const expandAccountInfo = ref(false)
const dropdownRef = ref(null)
const loading = ref(false)
const expertUser = ref<any>(null)

// Fetch expert settings data
const fetchExpertSettings = async () => {
  try {
    loading.value = true
    const response = await expertStore.fetchExpertProfile('');
    console.log(`[ExpertProfileDropdown] fetchExpertSettings`, response)
    expertUser.value = response.expert;
    
    // Update auth store user with fresh data if needed
    if (response.expert) {
      console.log('response.expert', response.expert)
      // Optionally sync some data back to auth store
      authStore.user = { ...authStore.user, ...expertUser.value }
    }
  } catch (error) {
    console.error('Failed to fetch expert settings:', error)
    // Fallback to auth store user if API fails
    expertUser.value = authStore.user
  } finally {
    loading.value = false
  }
}

// Dynamic computed properties (for avatar in button)
const userAvatarInfo = computed(() => {
  // Use name from mini profile composable, fallback to expert user data
  const name = miniProfile.name.value || 
               (expertUser.value ? `${expertUser.value.first_name || ''} ${expertUser.value.last_name || ''}`.trim() : '') ||
               (authStore.user ? `${authStore.user.first_name || ''} ${authStore.user.last_name || ''}`.trim() : '')
  return generateInitialsAvatar(name || 'User')
})

const shouldShowOldDashboardSwitch = computed(() => {
  return userVersion.value === null || userVersion.value <= 1
})

const switchToOldDashboard = async () => {
  try {
    const { data } = await authStore.switchToOldDashboard();
    window.location.href = data.url;
  } catch (err) {
    console.error('SSO switch failed', err);
  }
};

function toggleDropdown() {
  if (!isProfileDropdown.value) {
    // Only fetch data when opening dropdown
    fetchExpertSettings()
  }
  isProfileDropdown.value = !isProfileDropdown.value
}

onClickOutside(dropdownRef, () => {
  isProfileDropdown.value = false
})

const logout = async () => {
  authStore.logout()
  await router.push('/expert/login')
}
</script>